import React, { useState, useRef, useEffect } from "react";
import { MoreHorizontal, Clipboard, Download, Code, Camera, FileText } from "lucide-react";
import { useToast } from './ToastNotifications';
import jsPDF from 'jspdf';

const CompactExportDropdown = ({
    colors = [],
    paletteName = "",
    getCss,
    getSvg
}) => {
    const [copied, setCopied] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ openUpward: false, openLeftward: false });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const { showCopyToast, showDownloadToast } = useToast();

    // Calculate optimal dropdown position
    useEffect(() => {
        const updatePosition = () => {
            if (isDropdownOpen && buttonRef.current) {
                const buttonRect = buttonRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const dropdownHeight = 450; // Estimated dropdown height (increased for descriptions)
                const dropdownWidth = 320; // 80 * 4 = w-80

                // Check if dropdown would go below viewport
                const wouldGoBelow = buttonRect.bottom + dropdownHeight > viewportHeight - 20;

                // Check if dropdown would go past right edge
                const wouldGoPastRight = buttonRect.right - dropdownWidth < 20;

                setDropdownPosition({
                    openUpward: wouldGoBelow,
                    openLeftward: wouldGoPastRight
                });
            }
        };

        updatePosition();

        // Update position on scroll and resize
        if (isDropdownOpen) {
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isDropdownOpen]);

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(type);
            showCopyToast(paletteName, type);
            setTimeout(() => setCopied(""), 2000);
            setIsDropdownOpen(false);
        } catch (err) {
            console.error("Failed to copy: ", err);
        }
    };

    const downloadFile = (content, filename, contentType, format) => {
        const blob = new Blob([content], { type: contentType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsDropdownOpen(false);

        showDownloadToast(paletteName, format);
    };

    const generatePolaroidImage = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Polaroid dimensions (optimized for social media)
        const width = 1080;
        const height = 1200;
        const padding = 80; // White border
        const bottomPadding = 240; // Extra space at bottom for text and logo (polaroid style)

        canvas.width = width;
        canvas.height = height;

        // White polaroid background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        // Calculate color swatch dimensions
        const swatchArea = {
            x: padding,
            y: padding,
            width: width - (padding * 2),
            height: height - padding - bottomPadding
        };

        // Determine grid layout based on number of colors
        const numColors = colors.length;
        let cols, rows;

        if (numColors <= 4) {
            cols = numColors;
            rows = 1;
        } else if (numColors <= 6) {
            cols = 3;
            rows = 2;
        } else if (numColors <= 9) {
            cols = 3;
            rows = 3;
        } else if (numColors <= 12) {
            cols = 4;
            rows = 3;
        } else if (numColors <= 16) {
            cols = 4;
            rows = 4;
        } else {
            // For more colors, create a single row
            cols = Math.min(numColors, 8);
            rows = Math.ceil(numColors / cols);
        }

        const swatchWidth = swatchArea.width / cols;
        const swatchHeight = swatchArea.height / rows;
        const gap = 8;

        // Draw color swatches
        colors.slice(0, cols * rows).forEach((color, index) => {
            const col = index % cols;
            const row = Math.floor(index / cols);

            const x = swatchArea.x + (col * swatchWidth) + gap;
            const y = swatchArea.y + (row * swatchHeight) + gap;
            const w = swatchWidth - (gap * 2);
            const h = swatchHeight - (gap * 2);

            // Draw color swatch with rounded corners
            ctx.fillStyle = color;
            ctx.beginPath();
            const radius = 12;
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + w - radius, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
            ctx.lineTo(x + w, y + h - radius);
            ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
            ctx.lineTo(x + radius, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
            ctx.fill();

            // Add subtle shadow
            ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
            ctx.shadowBlur = 4;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 2;
        });

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Draw palette info in polaroid bottom space
        const textY = height - bottomPadding + 50;

        // Palette name
        ctx.fillStyle = '#1f2937';
        ctx.font = 'bold 48px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(paletteName || 'Color Palette', width / 2, textY);

        // Color count
        ctx.font = '32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillStyle = '#6b7280';
        ctx.fillText(`${numColors} colors`, width / 2, textY + 50);

        // Load and draw logo
        const logo = new Image();
        logo.onload = () => {
            // Draw logo centered, below color count
            const logoSize = 48;
            const logoX = (width / 2) - (logoSize / 2);
            const logoY = textY + 70;
            ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

            // Draw website URL below logo
            ctx.font = '24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
            ctx.fillStyle = '#9ca3af';
            ctx.textAlign = 'center';
            ctx.fillText('www.ko-mo-re-bi.com', width / 2, logoY + logoSize + 30);

            // Convert canvas to blob and download
            canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${paletteName || 'palette'}-polaroid.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                setIsDropdownOpen(false);
                showDownloadToast(paletteName, 'polaroid');
            }, 'image/png');
        };

        logo.onerror = () => {
            // If logo fails to load, still generate the image without it
            // Draw website URL in place of logo
            ctx.font = '26px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
            ctx.fillStyle = '#6b7280';
            ctx.textAlign = 'center';
            ctx.fillText('www.ko-mo-re-bi.com', width / 2, textY + 90);

            // Convert canvas to blob and download
            canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${paletteName || 'palette'}-polaroid.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                setIsDropdownOpen(false);
                showDownloadToast(paletteName, 'polaroid');
            }, 'image/png');
        };

        // Load logo from public folder
        logo.src = '/komorebi-logo.svg';
    };

    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const contentWidth = pageWidth - (margin * 2);
        const contentHeight = pageHeight - (margin * 2);

        // White background
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // Title
        doc.setFontSize(24);
        doc.setTextColor(31, 41, 55); // gray-900
        doc.text(paletteName || 'Color Palette', margin, margin + 10);

        // Subtitle
        doc.setFontSize(12);
        doc.setTextColor(107, 114, 128); // gray-500
        doc.text(`${colors.length} colors • Generated with Komorebi`, margin, margin + 18);

        // Calculate grid layout
        const numColors = colors.length;
        let cols, rows;

        if (numColors <= 6) {
            cols = numColors;
            rows = 1;
        } else if (numColors <= 12) {
            cols = 6;
            rows = 2;
        } else {
            cols = 6;
            rows = Math.ceil(numColors / 6);
        }

        // Calculate swatch dimensions
        const swatchAreaY = margin + 30;
        const swatchAreaHeight = contentHeight - 50;
        const gap = 3;
        const swatchWidth = (contentWidth - (gap * (cols - 1))) / cols;
        const swatchHeight = (swatchAreaHeight - (gap * (rows - 1))) / rows;

        // Draw color swatches
        colors.forEach((color, index) => {
            const col = index % cols;
            const row = Math.floor(index / cols);

            const x = margin + (col * (swatchWidth + gap));
            const y = swatchAreaY + (row * (swatchHeight + gap));

            // Parse hex color
            const hex = color.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);

            // Draw swatch with rounded corners
            doc.setFillColor(r, g, b);
            doc.roundedRect(x, y, swatchWidth, swatchHeight - 15, 2, 2, 'F');

            // Add color code below swatch
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            const textX = x + (swatchWidth / 2);
            doc.text(color.toUpperCase(), textX, y + swatchHeight - 8, { align: 'center' });

            // Add color name/number
            doc.setFontSize(7);
            doc.setTextColor(120, 120, 120);
            doc.text(`Color ${index + 1}`, textX, y + swatchHeight - 3, { align: 'center' });
        });

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(156, 163, 175); // gray-400
        doc.text('www.ko-mo-re-bi.com', pageWidth / 2, pageHeight - 8, { align: 'center' });

        // Save PDF
        doc.save(`${paletteName || 'palette'}.pdf`);
        setIsDropdownOpen(false);
        showDownloadToast(paletteName, 'pdf');
    };

    const generateDesignSVG = () => {
        // Enhanced SVG export optimized for design software (Figma, Sketch, Illustrator, Affinity)
        const swatchSize = 100;
        const gap = 10;
        const padding = 20;
        const textHeight = 30;
        const headerHeight = 60;

        const cols = Math.min(colors.length, 6);
        const rows = Math.ceil(colors.length / cols);

        const width = (cols * swatchSize) + ((cols - 1) * gap) + (padding * 2);
        const height = headerHeight + (rows * (swatchSize + textHeight)) + ((rows - 1) * gap) + (padding * 2);

        let svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <!-- Generated with Komorebi Color Palette Generator -->
  <!-- www.ko-mo-re-bi.com -->
  <defs>
    <style>
      .palette-title { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 20px; font-weight: 600; fill: #1f2937; }
      .palette-subtitle { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; fill: #6b7280; }
      .color-label { font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace; font-size: 11px; fill: #374151; }
      .color-name { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 9px; fill: #9ca3af; }
    </style>
  </defs>

  <!-- Background -->
  <rect width="${width}" height="${height}" fill="#ffffff" rx="8"/>

  <!-- Header -->
  <text x="${padding}" y="${padding + 20}" class="palette-title">${paletteName || 'Color Palette'}</text>
  <text x="${padding}" y="${padding + 38}" class="palette-subtitle">${colors.length} colors</text>

  <!-- Color Swatches -->
  <g id="palette-colors">
`;

        colors.forEach((color, index) => {
            const col = index % cols;
            const row = Math.floor(index / cols);

            const x = padding + (col * (swatchSize + gap));
            const y = headerHeight + padding + (row * (swatchSize + textHeight + gap));

            svg += `    <g id="color-${index + 1}" data-color="${color}">
      <rect x="${x}" y="${y}" width="${swatchSize}" height="${swatchSize}" fill="${color}" rx="6"/>
      <text x="${x + swatchSize / 2}" y="${y + swatchSize + 16}" text-anchor="middle" class="color-label">${color.toUpperCase()}</text>
      <text x="${x + swatchSize / 2}" y="${y + swatchSize + 26}" text-anchor="middle" class="color-name">Color ${index + 1}</text>
    </g>
`;
        });

        svg += `  </g>
</svg>`;

        return svg;
    };

    // Generate different formats
    const generateFormats = () => {
        const hexColors = colors.map(c => c).join(', ');
        const hexArray = colors.map(c => `"${c}"`).join(', ');

        const rgbColors = colors.map(color => {
            const hex = color.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            return `rgb(${r}, ${g}, ${b})`;
        });

        const hslColors = colors.map(color => {
            const hex = color.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16) / 255;
            const g = parseInt(hex.substr(2, 2), 16) / 255;
            const b = parseInt(hex.substr(4, 2), 16) / 255;

            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            let h, s, l = (max + min) / 2;

            if (max === min) {
                h = s = 0;
            } else {
                const d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                switch (max) {
                    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                    case g: h = (b - r) / d + 2; break;
                    case b: h = (r - g) / d + 4; break;
                }
                h /= 6;
            }

            return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
        });

        return {
            hex: hexColors,
            hexArray: `[${hexArray}]`,
            rgb: rgbColors.join(', '),
            hsl: hslColors.join(', '),
            css: getCss ? getCss() : `:root {\n${colors.map((color, i) => `  --color-${i + 1}: ${color};`).join('\n')}\n}`,
            svg: getSvg ? getSvg() : generateDesignSVG(),
            scss: `$colors: (\n${colors.map((color, i) => `  'color-${i + 1}': ${color}`).join(',\n')}\n);`,
            js: `const colors = [${hexArray}];`,
            sketch: JSON.stringify({
                compatibleVersion: "2.0",
                pluginVersion: "2.0",
                colors: colors.map((color, i) => ({
                    name: `Color ${i + 1}`,
                    red: parseInt(color.substr(1, 2), 16) / 255,
                    green: parseInt(color.substr(3, 2), 16) / 255,
                    blue: parseInt(color.substr(5, 2), 16) / 255,
                    alpha: 1
                }))
            }, null, 2)
        };
    };

    const formats = generateFormats();

    const exportOptions = [
        {
            id: 'css',
            label: 'Copy CSS Variables',
            icon: Clipboard,
            action: () => copyToClipboard(formats.css, 'css'),
            group: 'copy'
        },
        {
            id: 'scss',
            label: 'Copy SCSS Map',
            icon: Clipboard,
            action: () => copyToClipboard(formats.scss, 'scss'),
            group: 'copy'
        },
        {
            id: 'js',
            label: 'Copy JavaScript',
            icon: Code,
            action: () => copyToClipboard(formats.js, 'array'),
            group: 'copy'
        },
        {
            id: 'rgb',
            label: 'Copy RGB Values',
            icon: Clipboard,
            action: () => copyToClipboard(formats.rgb, 'rgb'),
            group: 'copy'
        },
        {
            id: 'hsl',
            label: 'Copy HSL Values',
            icon: Clipboard,
            action: () => copyToClipboard(formats.hsl, 'hsl'),
            group: 'copy'
        },
        {
            id: 'svg',
            label: 'SVG (Figma, Illustrator)',
            description: 'Vector format for design software',
            icon: Download,
            action: () => downloadFile(formats.svg, `${paletteName || 'palette'}.svg`, 'image/svg+xml', 'svg'),
            group: 'design'
        },
        {
            id: 'pdf',
            label: 'PDF (Universal)',
            description: 'Print-ready format for all design software',
            icon: FileText,
            action: generatePDF,
            group: 'design'
        },
        {
            id: 'sketch',
            label: 'Sketch Palette',
            description: 'Native color palette for Sketch',
            icon: Download,
            action: () => downloadFile(formats.sketch, `${paletteName || 'palette'}.sketchpalette`, 'application/json', 'sketch'),
            group: 'design'
        },
        {
            id: 'polaroid',
            label: 'Social Media Image',
            description: 'Share on Instagram, Twitter, etc.',
            icon: Camera,
            action: generatePolaroidImage,
            group: 'share'
        }
    ];

    const copyOptions = exportOptions.filter(option => option.group === 'copy');
    const designOptions = exportOptions.filter(option => option.group === 'design');
    const shareOptions = exportOptions.filter(option => option.group === 'share');

    // Calculate dropdown classes based on position
    const getDropdownClasses = () => {
        let classes = "fixed w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[9999]";

        if (dropdownPosition.openUpward) {
            classes += " mb-2"; // margin bottom instead of top
        } else {
            classes += " mt-2"; // margin top (default)
        }

        return classes;
    };

    const getDropdownStyle = () => {
        if (!buttonRef.current || !isDropdownOpen) return {};

        const buttonRect = buttonRef.current.getBoundingClientRect();
        let style = {};

        if (dropdownPosition.openUpward) {
            // Position above the button
            style.bottom = window.innerHeight - buttonRect.top + 8;
        } else {
            // Position below the button (default)
            style.top = buttonRect.bottom + 8;
        }

        if (dropdownPosition.openLeftward) {
            // Align to the left edge of the button
            style.left = buttonRect.left;
        } else {
            // Align to the right edge of the button (default)
            style.right = window.innerWidth - buttonRect.right;
        }

        return style;
    };

    return (
        <div className="relative inline-block">
            <button
                ref={buttonRef}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                title="Export options"
            >
                <MoreHorizontal className="h-4 w-4" />
            </button>

            {isDropdownOpen && (
                <div
                    ref={dropdownRef}
                    className={getDropdownClasses()}
                    style={getDropdownStyle()}
                >
                    <div className="p-3">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            Copy to Clipboard
                        </div>
                        <div className="space-y-1">
                            {copyOptions.map((option) => {
                                const Icon = option.icon;
                                const isActive = copied === option.id;

                                return (
                                    <button
                                        key={option.id}
                                        onClick={option.action}
                                        className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-all duration-200 ${isActive
                                            ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            }`}
                                    >
                                        <Icon className="w-4 h-4 mr-3" />
                                        {isActive ? 'Copied!' : option.label}
                                    </button>
                                );
                            })}
                        </div>

                        {designOptions.length > 0 && (
                            <>
                                <div className="border-t border-gray-200 dark:border-gray-600 my-3"></div>
                                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                    Design Software
                                </div>
                                <div className="space-y-1">
                                    {designOptions.map((option) => {
                                        const Icon = option.icon;

                                        return (
                                            <button
                                                key={option.id}
                                                onClick={option.action}
                                                className="w-full flex flex-col items-start px-3 py-2 text-sm rounded-md transition-all duration-200 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                title={option.description}
                                            >
                                                <div className="flex items-center w-full">
                                                    <Icon className="w-4 h-4 mr-3 flex-shrink-0" />
                                                    <span className="font-medium">{option.label}</span>
                                                </div>
                                                {option.description && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-7 mt-0.5">
                                                        {option.description}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {shareOptions.length > 0 && (
                            <>
                                <div className="border-t border-gray-200 dark:border-gray-600 my-3"></div>
                                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                    Share & Export
                                </div>
                                <div className="space-y-1">
                                    {shareOptions.map((option) => {
                                        const Icon = option.icon;

                                        return (
                                            <button
                                                key={option.id}
                                                onClick={option.action}
                                                className="w-full flex flex-col items-start px-3 py-2 text-sm rounded-md transition-all duration-200 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                                title={option.description}
                                            >
                                                <div className="flex items-center w-full">
                                                    <Icon className="w-4 h-4 mr-3 flex-shrink-0" />
                                                    <span className="font-medium">{option.label}</span>
                                                </div>
                                                {option.description && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-7 mt-0.5">
                                                        {option.description}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Overlay to close dropdown */}
            {isDropdownOpen && (
                <div
                    className="fixed inset-0 z-[9998]"
                    onClick={() => setIsDropdownOpen(false)}
                />
            )}
        </div>
    );
};

export default CompactExportDropdown;